'use client';

import {
  useState,
  useCallback,
  useEffect,
  useContext,
  type FC,
  ReactNode,
  Fragment,
} from 'react';
import { TaxReturnActivityLogDialogProps } from './TaxReturnActivityLogDialog.types';
import {
  ActivityItem,
  Button,
  ButtonGroup,
  Dialog,
  EmptyState,
  Flex,
  Separator,
} from '@/_components';
import { getTaxReturnActivityLogs } from '@/_api/admin-tax-returns';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { Activity } from '@/_types/models/activitites';
import styles from './TaxReturnActivityLogDialog.module.css';

export const TaxReturnActivityLogDialog: FC<
  TaxReturnActivityLogDialogProps
> = ({ isOpen, onClose }) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [activityLogs, setActivityLogs] = useState<Array<Activity>>([]);

  const { selectedTaxReturnForActivity } = useContext(AdminTaxReturnsContext);
  const { token } = useContext(UserContext);

  const getActivityLogs = useCallback(
    async (id: number, companyId: number) => {
      if (typeof id !== 'number' || typeof companyId !== 'number' || !token) {
        return;
      }

      try {
        setLoading(true);
        const logs = await getTaxReturnActivityLogs({
          id,
          companyId,
          token,
        });
        setActivityLogs(logs);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    if (selectedTaxReturnForActivity?.id) {
      getActivityLogs(
        selectedTaxReturnForActivity?.id,
        selectedTaxReturnForActivity?.company_id,
      );
    }
  }, [
    getActivityLogs,
    selectedTaxReturnForActivity?.company_id,
    selectedTaxReturnForActivity?.id,
  ]);

  const isEmpty: boolean = activityLogs?.length === 0 && !isLoading;

  const getContent = (): ReactNode => {
    if (isEmpty || isLoading) {
      return (
        <EmptyState
          icon="file"
          description={isLoading ? 'Loading...' : 'No related activities'}
        />
      );
    }

    return (
      <Flex
        isColumn={true}
        paddingY={16}
        className={styles.activitiesTaxReturn}
      >
        {activityLogs?.map((activityLog, key) => {
          const isLast: boolean = key !== activityLogs.length - 1;

          return (
            <Fragment key={key}>
              <ActivityItem key={key} activityLog={activityLog} />
              {isLast && (
                <Separator
                  isLight={true}
                  className={styles.activitiesTaxReturn__separator}
                />
              )}
            </Fragment>
          );
        })}
      </Flex>
    );
  };

  const content: ReactNode = getContent();

  if (!selectedTaxReturnForActivity) return null;

  return (
    <Dialog
      isOpen={isOpen}
      disablePadding={true}
      onClose={onClose}
      title={
        !!selectedTaxReturnForActivity?.tax_return_name
          ? `Activity log for ${selectedTaxReturnForActivity?.tax_return_name}`
          : 'Activity log'
      }
    >
      <Dialog.Content>{content}</Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button value="Close" intent="secondary" onClick={onClose} />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
